// The weekend/holiday shift of scheduled payments, proposed for confirmation.
//
// The cash-flow grid places an occurrence on the date its recurring rule
// produces, and that date is regularly a day the bank is shut: a rent anchored
// to the 15th lands on a Saturday twice a year, a biweekly payroll walks into
// Canada Day. Showing the money leaving on a day it cannot clear makes every
// balance after it wrong.
//
// The fix is a stored one-off SchedulePaymentException moving the occurrence to
// the next banking day — a real change to when money leaves, so it is ASKED,
// not assumed. This hook finds the affected occurrences and hands them to the
// page; the page shows them in a modal and calls `confirm` if the user agrees.
// The recurring anchor is untouched either way, so only that occurrence moves.

import { useCallback, useEffect, useRef, useState } from "react";

import { useBulkSchedulePaymentExceptions } from "@api/hooks";
import { showApiError, showSuccess } from "@common";

import { collectNonBankingMoves } from "./scheduleOccurrences";

const sameMoves = (a, b) =>
  a.length === b.length &&
  a.every((move, i) => move.key === b[i].key && move.newDate === b[i].newDate);

/**
 * Collect the occurrences in `occurrencesByKey` that fall on a weekend or an
 * Ontario bank holiday, and expose the confirm/dismiss pair that resolves them.
 *
 * @param {Object} occurrencesByKey  `occurrencesByClinic` / `occurrencesByAccount`
 * @param {{ enabled?: boolean }} options
 *   enabled — gate on the caller's write permission. A read-only viewer must not
 *   be prompted to make a change it would only be refused for.
 * @returns {{ pending: object[], saving: boolean,
 *             confirm: () => Promise<void>, dismiss: () => void }}
 */
export default function useNonBankingMovePrompt(occurrencesByKey, { enabled = true } = {}) {
  // ONE request for the whole batch, not one per payment: a month can put a
  // dozen payments on closed days, and as individual POSTs that is a dozen round
  // trips whose partial failure leaves half the moves applied with no record of
  // which half. The bulk endpoint writes them in a single transaction.
  const bulkExceptions = useBulkSchedulePaymentExceptions();

  // Every occurrence already answered for — moved or declined. Kept in a ref, not
  // state, because it must not itself trigger a render: dismissing has to stop
  // the prompt coming back on the next refetch, and a confirmed move that failed
  // (a forbidden role, an exception another tab just wrote) would fail again on
  // the very next render, so neither is ever re-proposed.
  const settled = useRef(new Set());
  const [pending, setPending] = useState([]);

  useEffect(() => {
    if (!enabled) {
      setPending([]);
      return;
    }
    const next = collectNonBankingMoves(occurrencesByKey).filter(
      (move) => !settled.current.has(move.key),
    );
    // Replaced only when the proposal actually differs. A refetch returning the
    // same data yields an equal-but-new array, and storing it would re-render
    // the open modal — resetting it under the user mid-decision.
    setPending((prev) => (sameMoves(prev, next) ? prev : next));
  }, [occurrencesByKey, enabled]);

  const dismiss = useCallback(() => {
    setPending((prev) => {
      prev.forEach((move) => settled.current.add(move.key));
      return [];
    });
  }, []);

  /**
   * Write the batch. `edited` is what the modal's rows ended up holding — the
   * proposed dates are a starting point the user can change, so the ARGUMENT is
   * the source of truth here and `pending` is only the fallback for a caller
   * that passed nothing.
   */
  const confirm = useCallback(
    async (edited) => {
      const moves = Array.isArray(edited) && edited.length ? edited : pending;
      if (!moves.length) return;
      // Settled BEFORE the request goes out, not after it lands: a re-render
      // between send and response would otherwise find these occurrences still
      // sitting on a closed day and prompt for them a second time.
      //
      // The WHOLE prompted batch is settled, not just the rows being written. A
      // row the user dropped was answered too — "leave this one alone" — and
      // re-asking on the next refetch would make that answer meaningless.
      //
      // `pending` is deliberately NOT cleared here. Clearing it would unmount the
      // modal the instant Move was clicked, so the request would run with no sign
      // it was running; the `settled` ref already prevents the re-prompt, and the
      // effect above leaves an unchanged proposal alone. The modal closes when the
      // request is done.
      pending.forEach((move) => settled.current.add(move.key));
      moves.forEach((move) => settled.current.add(move.key));

      try {
        await bulkExceptions.mutateAsync(
          moves.map((move) => ({
            schedule: move.scheduleId,
            original_date: move.originalDate,
            new_date: move.newDate,
            // Sent only when the amount was actually edited, so a date-only move
            // leaves the schedule's own amount (or an existing override) alone
            // rather than pinning a duplicate of it to this occurrence.
            ...(move.newAmount != null ? { amount: move.newAmount } : {}),
          })),
        );
        // Counted from what was actually written — rows dropped in the modal are
        // not in `moves`, and claiming them would overstate what happened. The
        // dates are the user's now, so the message no longer names one.
        const count = moves.length;
        showSuccess(`${count} ${count === 1 ? "payment" : "payments"} rescheduled.`);
      } catch (err) {
        // The batch is one transaction, so a failure moved NOTHING — there is no
        // partial state to describe, and the payments stay where the rule put them.
        showApiError(err);
      } finally {
        setPending([]);
      }
    },
    [pending, bulkExceptions],
  );

  return { pending, saving: bulkExceptions.isPending, confirm, dismiss };
}
